import { useRef, useState, type ReactNode } from 'react';
import { Icon } from './Icon';

/* ==========================================================================
   شارة الحالة
   ========================================================================== */

export function Badge({
  kind = 'neutral',
  children,
}: {
  kind?: string;
  children: ReactNode;
}) {
  return <span className={`badge badge-${kind}`}>{children}</span>;
}

/* ==========================================================================
   رأس الصفحة
   ========================================================================== */

export function PageHead({
  title,
  subtitle,
  actions,
  avatar,
}: {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
  /**
   * صورة السجل إلى جانب اسمه.
   *
   * تُمرَّر مكوّنًا لا اسمًا: الصورة تُولَّد من الاسم في شاشات، وقد تكون
   * ملفًا مرفوعًا في غيرها، وهذا المكوّن لا شأن له بأيّهما.
   */
  avatar?: ReactNode;
}) {
  return (
    <div className="page-head">
      <div className="page-head-main">
        {avatar}
        <div>
          <h1 className="page-title">{title}</h1>
          {subtitle && <div className="page-sub">{subtitle}</div>}
        </div>
      </div>
      {actions && <div className="page-actions">{actions}</div>}
    </div>
  );
}

/* ==========================================================================
   حقل البحث
   ========================================================================== */

export function SearchBox({
  value,
  onChange,
  placeholder = 'بحث',
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <div className="search">
      <Icon name="search" size={15} />
      <input
        className="input"
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
      />
    </div>
  );
}

/* ==========================================================================
   حالات القائمة
   ========================================================================== */

export function TableSkeleton({ rows = 6, cols = 5 }: { rows?: number; cols?: number }) {
  return (
    <tbody>
      {Array.from({ length: rows }, (_, r) => (
        <tr key={r}>
          {Array.from({ length: cols }, (_, c) => (
            <td key={c}>
              <div className="skeleton" style={{ height: 12, width: c === 0 ? '70%' : '45%' }} />
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  );
}

export function EmptyState({
  title,
  text,
  action,
}: {
  title: string;
  text?: string;
  action?: ReactNode;
}) {
  return (
    <div className="empty">
      <div className="empty-title">{title}</div>
      {text && <p className="empty-text">{text}</p>}
      {action}
    </div>
  );
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const message = error instanceof Error ? error.message : 'حدث خطأ غير متوقع.';
  return (
    <div className="empty">
      <div className="empty-title">تعذّر تحميل البيانات</div>
      <p className="empty-text">{message}</p>
      {onRetry && (
        <button className="btn" onClick={onRetry}>
          <Icon name="refresh" size={15} />
          إعادة المحاولة
        </button>
      )}
    </div>
  );
}

/* ==========================================================================
   الترقيم
   ========================================================================== */

/** الأحجام المتاحة للصفحة — والسقف مئة كما يفرضه مخطط الاستعلام. */
const PAGE_SIZES = [10, 25, 50, 100] as const;

export function Pager({
  page,
  pageCount,
  total,
  pageSize,
  onPage,
  onPageSize,
}: {
  page: number;
  pageCount: number;
  total: number;
  pageSize: number;
  onPage: (p: number) => void;
  /** حين يُمرَّر يظهر اختيار حجم الصفحة. */
  onPageSize?: (size: number) => void;
}) {
  /*
   * حقل رقم الصفحة له حالته الخاصة.
   *
   * ربطه بالصفحة الحالية مباشرةً يجعل كل حرف يُكتب انتقالًا: من يريد الصفحة
   * الثانية عشرة يكتب «١» فينتقل إلى الأولى، ثم «٢» فيصير «١٢»… وقد تبدّلت
   * الشاشة تحته مرتين. فيُكتب هنا ويُنفَّذ عند Enter أو مغادرة الحقل.
   */
  const [typed, setTyped] = useState('');

  if (total === 0) return null;

  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  const jump = () => {
    const n = Number(typed.replace(/[^\d]/g, ''));
    setTyped('');
    if (!n || n === page) return;
    /* القصّ داخل المدى: رقمٌ خارجه ينتقل إلى أقرب طرف بدل أن يُرفض بصمت. */
    onPage(Math.max(1, Math.min(n, pageCount)));
  };

  return (
    <div className="pager">
      <span>
        عرض <span className="num">{from}</span> إلى <span className="num">{to}</span> من{' '}
        <span className="num">{total}</span>
      </span>

      {onPageSize && (
        <label className="pager-size">
          <span className="tiny mute">لكل صفحة</span>
          <select
            className="select select-sm"
            value={pageSize}
            onChange={(e) => onPageSize(Number(e.target.value))}
            aria-label="عدد السجلات في الصفحة"
          >
            {PAGE_SIZES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>
      )}

      <div className="pager-end">
        {/* الانتقال المباشر: مئة صفحة لا تُتصفَّح بزرّ «التالي». */}
        {pageCount > 1 && (
          <label className="pager-jump">
            <span className="tiny mute">انتقل إلى</span>
            <input
              className="input input-sm num"
              inputMode="numeric"
              placeholder={String(page)}
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  jump();
                }
              }}
              onBlur={jump}
              aria-label={`رقم الصفحة، من ١ إلى ${pageCount}`}
            />
          </label>
        )}

        <button
          className="btn btn-sm btn-icon"
          onClick={() => onPage(page - 1)}
          disabled={page <= 1}
          aria-label="الصفحة السابقة"
        >
          {/* في الاتجاه من اليمين لليسار يشير السابق إلى اليمين. */}
          <Icon name="chevronRight" size={15} />
        </button>
        <span className="num small">
          {page} / {pageCount}
        </span>
        <button
          className="btn btn-sm btn-icon"
          onClick={() => onPage(page + 1)}
          disabled={page >= pageCount}
          aria-label="الصفحة التالية"
        >
          <Icon name="chevronLeft" size={15} />
        </button>
      </div>
    </div>
  );
}

/* ==========================================================================
   غلافُ صنفٍ في الكتالوج
   ========================================================================== */

/**
 * صورةٌ للصنف تُختار من الجهاز وتُعرض غلافًا.
 *
 * وتُخزَّن سلسلةَ بياناتٍ مضمَّنة كما يُخزَّن شعارُ الشركة: لا خدمةَ
 * تخزينٍ خارجيّة، فلا رابطٌ ينكسر بعد سنةٍ ولا ملفٌّ يُنسى في مجلّد. والحدُّ
 * يُفحص هنا قبل الرفع — رفضُ الخادم بعد رفع ملفٍ كبير يهدر وقتَ المستخدم
 * ويترك السببَ غامضًا.
 */
export function CoverField({
  value,
  onChange,
  label = 'صورة الغلاف',
  hint = 'اسحب صورةً هنا أو انقر للاختيار — تظهر غلافًا لبطاقة الصنف.',
  maxBytes = 500_000,
  compact,
}: {
  value: string;
  onChange: (v: string) => void;
  label?: string;
  hint?: string;
  maxBytes?: number;
  /** مربّعٌ صغيرٌ في رأس النموذج بدل مساحةٍ بمقاس الغلاف. */
  compact?: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [over, setOver] = useState(false);

  const take = (file: File | undefined) => {
    setError(null);
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('الملفُّ ليس صورة.');
      return;
    }
    if (file.size > maxBytes) {
      setError(
        `حجم الملف ${Math.round(file.size / 1024)} كيلوبايت، والحد ${Math.round(maxBytes / 1024)}. اضغط الصورة أو استخدم مقاسًا أصغر.`,
      );
      return;
    }
    const reader = new FileReader();
    reader.onload = () => onChange(String(reader.result));
    reader.onerror = () => setError('تعذّرت قراءة الملف.');
    reader.readAsDataURL(file);
  };

  return (
    <div className={`field${compact ? ' fx-cover' : ''}`}>
      {!compact && <span className="label">{label}</span>}

      {/*
        المساحةُ بمقاس الغلاف ونسبته: ما يُرى هنا هو ما سيُرى في البطاقة.
        ومربّعٌ صغيرٌ بجانب زرٍّ لا يقول ذلك.

        فإن كان في رأس النموذج صغُر: الرأسُ للتعريف لا للتحرير، ومساحةٌ
        كبيرةٌ فيه تزحم ما جاء القارئُ من أجله.
      */}
      <div
        className={`drop${compact ? ' is-sm' : ''}${value ? ' has-img' : ''}${over ? ' is-over' : ''}`}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setOver(false);
          take(e.dataTransfer.files?.[0]);
        }}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
        aria-label={label}
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/svg+xml"
          style={{ display: 'none' }}
          onChange={(e) => {
            take(e.target.files?.[0]);
            e.target.value = '';
          }}
        />

        {value ? (
          <>
            <img src={value} alt="" />
            <span className="drop-tools">
              {compact ? (
                <button
                  type="button"
                  className="btn btn-sm btn-icon btn-danger"
                  aria-label="إزالة الغلاف"
                  onClick={(e) => {
                    e.stopPropagation();
                    setError(null);
                    onChange('');
                  }}
                >
                  <Icon name="x" size={13} />
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    className="btn btn-sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      inputRef.current?.click();
                    }}
                  >
                    <Icon name="refresh" size={13} />
                    تغيير
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-danger"
                    onClick={(e) => {
                      e.stopPropagation();
                      setError(null);
                      onChange('');
                    }}
                  >
                    <Icon name="trash" size={13} />
                    إزالة
                  </button>
                </>
              )}
            </span>
          </>
        ) : (
          <span className="drop-hint">
            <Icon name="folder" size={compact ? 16 : 22} />
            {compact ? 'غلاف' : hint}
          </span>
        )}
      </div>

      {error && (
        <span className="tiny" style={{ color: 'var(--danger)' }}>
          {error}
        </span>
      )}
    </div>
  );
}

/* ==========================================================================
   رأسُ نموذجِ صنف
   ========================================================================== */

/**
 * الغلافُ والحالةُ في رأس النموذج لا في ذيله.
 *
 * هما ما يُعرَف به الصنفُ قبل حقوله: صورتُه، وهل يُعرض أصلًا. وكانا في
 * آخر النموذج فيُمرَّر إليهما، ويأخذ الغلافُ منه مساحةً بمقاس البطاقة
 * تزحم ما جاء القارئُ من أجله. فصعِدا إلى الرأس وصغُرا: مربّعٌ يُنقر
 * ومفتاحٌ يُقلب، وسطرٌ يفصلهما عمّا تحتهما.
 */
export function FormTop({
  image,
  onImage,
  active,
  onActive,
  maxBytes,
  onLabel,
  offLabel,
  onNote,
  offNote,
}: {
  image: string;
  onImage: (v: string) => void;
  active: boolean;
  onActive: (v: boolean) => void;
  maxBytes?: number;
  onLabel?: string;
  offLabel?: string;
  onNote?: string;
  offNote?: string;
}) {
  return (
    <div className="fx-top">
      <CoverField compact value={image} onChange={onImage} maxBytes={maxBytes} />
      <span className="fx-top-note tiny mute">
        الغلافُ يظهر أعلى بطاقة الصنف في الكتالوج — انقر المربّع أو اسحب
        صورةً إليه.
      </span>
      <label className="sw is-sm">
        <input
          type="checkbox"
          checked={active}
          onChange={(e) => onActive(e.target.checked)}
        />
        <span className="sw-track">
          <span className="sw-dot" />
        </span>
        <span className="sw-text">
          <b>{active ? (onLabel ?? 'نشطة') : (offLabel ?? 'موقوفة')}</b>
          <span className="tiny mute">
            {active
              ? (onNote ?? 'تظهر للاختيار في المستندات الجديدة')
              : (offNote ?? 'تبقى في المستندات القديمة ولا تُختار في الجديد')}
          </span>
        </span>
      </label>
    </div>
  );
}

/* ==========================================================================
   وجهُ الكتالوج: بطاقاتٌ أو جدول
   ========================================================================== */

/**
 * الكتالوجُ يُتصفَّح لا يُدقَّق — والوجهُ يُحفظ.
 *
 * الجدولُ يُجيب «كم سعرُ كذا» حين تعرف اسمَه، ولا يُجيب «ماذا نبيع» — وهو
 * ما يُسأل عند إعداد عرضٍ لعميلٍ جديد. فوجهان: بطاقاتٌ تُقرأ بالنظر،
 * وجدولٌ يبقى لمن يقارن صفًّا بصفّ. وإعادةُ الاختيار في كلّ زيارةٍ تُتعب
 * من له وجهٌ يفضّله، فيُحفظ.
 */
export type CatView = 'cards' | 'table';

export function useCatView(key: string): [CatView, (v: CatView) => void] {
  const [view, setView] = useState<CatView>(() => {
    try {
      return localStorage.getItem(key) === 'table' ? 'table' : 'cards';
    } catch {
      return 'cards';
    }
  });
  const save = (v: CatView) => {
    setView(v);
    try {
      localStorage.setItem(key, v);
    } catch {
      /* المتصفّحُ قد يمنع التخزين — والوجهُ يبقى لهذه الجلسة. */
    }
  };
  return [view, save];
}

export function CatViewSwitch({
  view,
  onView,
}: {
  view: CatView;
  onView: (v: CatView) => void;
}) {
  return (
    <span className="seg" role="group" aria-label="شكل العرض">
      <button
        type="button"
        className={`seg-btn${view === 'cards' ? ' is-on' : ''}`}
        onClick={() => onView('cards')}
        title="بطاقات"
        aria-label="بطاقات"
      >
        <Icon name="layers" size={14} />
      </button>
      <button
        type="button"
        className={`seg-btn${view === 'table' ? ' is-on' : ''}`}
        onClick={() => onView('table')}
        title="جدول"
        aria-label="جدول"
      >
        <Icon name="listUl" size={14} />
      </button>
    </span>
  );
}

/* ==========================================================================
   حقلٌ يقترح ما سبق
   ========================================================================== */

/**
 * حقلٌ يُكتب فيه ويُختار منه.
 *
 * التصنيفاتُ والوحداتُ لا تُدار في شاشةِ إعداد: أوّلُ من يكتب «تصوير»
 * ينشئها، ومن بعده يجدها في القائمة. وقائمةٌ مغلقةٌ كانت ستوجب فتحَ شاشةٍ
 * لإضافة كلمة، وحقلٌ حرٌّ كان سيُنتج «تصوير» و«تصوير ». فالحقلُ حرٌّ
 * ومعه ما سبق.
 *
 * و«datalist» لا «select»: الاختيارُ لا يمنع الكتابة، والمتصفّح يرشّح
 * المقترحات بما يُكتب.
 */
export function Suggest({
  id,
  value,
  onChange,
  options,
  placeholder,
  dir,
}: {
  id: string;
  value: string;
  onChange: (v: string) => void;
  options: readonly string[];
  placeholder?: string;
  dir?: 'ltr' | 'rtl';
}) {
  const listId = `${id}-options`;
  return (
    <>
      <input
        id={id}
        className="input"
        list={listId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        dir={dir}
        autoComplete="off"
      />
      <datalist id={listId}>
        {options.map((o) => (
          <option key={o} value={o} />
        ))}
      </datalist>
    </>
  );
}

/* ==========================================================================
   تنبيه
   ========================================================================== */

/**
 * تنبيهٌ يُقرأ لا يصرخ.
 *
 * الشريطُ الملوَّن كلُّه — أرضيّةً وحرفًا وإطارًا — يُتعب العين ويجعل
 * ثلاثةَ تنبيهاتٍ جدارًا من اللون لا يُقرأ منه شيء. فاللونُ يبقى في
 * حافّةٍ وأيقونةٍ يُعرف بهما جنسُ الخبر، والنصُّ بحبره المعتاد يُقرأ
 * كما يُقرأ سائرُ الكلام.
 *
 * وله ذيلٌ لفعلٍ واحد: تنبيهٌ يقول «اضبط» ولا يدلّ على أين، يترك قارئَه
 * يبحث.
 */
export function Notice({
  kind = 'info',
  icon = 'info',
  title,
  action,
  children,
}: {
  kind?: 'info' | 'warning' | 'danger' | 'success';
  icon?: string;
  title: string;
  action?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <div className={`notice notice-${kind}`}>
      <span className="notice-icon" aria-hidden="true">
        <Icon name={icon} size={15} />
      </span>
      <div className="notice-body">
        <strong className="notice-title">{title}</strong>
        {children && <div className="notice-text">{children}</div>}
      </div>
      {action && <div className="notice-act">{action}</div>}
    </div>
  );
}

/* ==========================================================================
   بطاقة إحصاء
   ========================================================================== */

export function Stat({
  label,
  value,
  note,
}: {
  label: string;
  value: ReactNode;
  note?: string;
}) {
  return (
    <div className="stat">
      <div className="stat-label">{label}</div>
      <div className="stat-value">{value}</div>
      {note && <div className="stat-note">{note}</div>}
    </div>
  );
}

/* ==========================================================================
   نافذة حوارية
   ========================================================================== */

export function Modal({
  title,
  onClose,
  children,
  footer,
  wide = false,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
}) {
  return (
    <div
      className="modal-scrim"
      onMouseDown={(e) => {
        // الإغلاق بالنقر خارجها فقط، لا عند سحب التحديد من الداخل إلى الخارج.
        if (e.target === e.currentTarget) onClose();
      }}
      role="presentation"
    >
      <div
        className={`modal${wide ? ' modal-wide' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className="modal-head">
          <h2 className="card-title">{title}</h2>
          <button
            className="btn btn-icon btn-ghost"
            onClick={onClose}
            aria-label="إغلاق"
            type="button"
          >
            <Icon name="x" size={17} />
          </button>
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-foot">{footer}</div>}
      </div>
    </div>
  );
}
